import moment from 'moment';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { toastDismissalTimeout } from '@shared/*';
import { ConfType, Page, StatusType, ToastType, Toast } from '@models/*';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/registration';
import { getToasts } from '../../store/selectors';
import { AddToast, DismissToast } from '../../store/actions';

@Injectable({
  providedIn: 'root'
})
/**
 * @deprecated use {@link ZenithToastService} instead
 */
export class ToastService {
  private toasts: Toast[];
  private dismissStats: {
    lastDismissalTime: number;
    nextInLine: number | null;
    scheduledToastIds: number[];
  };

  constructor(private readonly translateService: TranslateService, private readonly store: Store<AppState>) {
    this.dismissStats = {
      lastDismissalTime: 0,
      nextInLine: null,
      scheduledToastIds: []
    };

    this.store
      .select(getToasts)
      .pipe(
        map((newToasts: Toast[]): number => {
          let newToastId = null;
          if (newToasts && this.toasts && newToasts.length > this.toasts.length) {
            newToastId = newToasts
              .filter(
                (elem) =>
                  !this.toasts.includes(elem) && elem.type !== ToastType.RELOAD && elem.type !== ToastType.UNAVAILABLE
              )
              .map((elem) => elem.id)[0];
          }
          this.toasts = newToasts;

          return newToastId;
        }),
        mergeMap((newToastId) => {
          if (newToastId) {
            this.dismissStats.scheduledToastIds = [...this.dismissStats.scheduledToastIds, newToastId];
            return timer(toastDismissalTimeout.long).pipe(switchMap(() => this.handleToastDismissal(newToastId)));
          }
          return of(true);
        })
      )
      .subscribe();
  }

  private handleToastDismissal(toastId: number): Observable<any> {
    const now = moment().valueOf();
    const timeDiff = now - this.dismissStats.lastDismissalTime;

    if (!this.dismissStats.nextInLine || this.dismissStats.nextInLine === toastId) {
      if (timeDiff >= toastDismissalTimeout.short) {
        this.dismissStats.scheduledToastIds = this.dismissStats.scheduledToastIds.filter((id) => id !== toastId);
        this.dismissStats.scheduledToastIds.length
          ? (this.dismissStats.nextInLine = this.dismissStats.scheduledToastIds[0])
          : (this.dismissStats.nextInLine = null);
        this.dismissStats.lastDismissalTime = now;
        this.dismissToast(toastId);
        return of(true);
      } else {
        return timer(toastDismissalTimeout.short - timeDiff).pipe(switchMap(() => this.handleToastDismissal(toastId)));
      }
    } else {
      return timer(toastDismissalTimeout.short).pipe(switchMap(() => this.handleToastDismissal(toastId)));
    }
  }

  public generateErrorToast(error: HttpErrorResponse): void {
    let newType: ToastType;
    let newMessage;

    switch (error.status.toString()[0]) {
      case '5': {
        newType = ToastType.RELOAD;
        newMessage = this.translateService.instant('MESSAGE.TOAST_RELOAD');
        break;
      }

      case '4': {
        newType = ToastType.HTTP_ERROR;
        newMessage = this.translateService.instant('MESSAGE.TOAST_HTTP_ERROR');
        break;
      }

      case '0': {
        newType = ToastType.UNAVAILABLE;
        newMessage = this.translateService.instant('MESSAGE.TOAST_UNAVAILABLE');
        break;
      }
    }

    this.generateToast(newType, newMessage);
  }
  public generatePageRedirectToast(fromPage: Page, reason?: StatusType): void {
    let newMessage;

    if (reason === StatusType.CANCELLED) {
      newMessage = this.translateService.instant('REDIRECT_TOUR_CANCELLED');
    } else {
      newMessage = this.translateService.instant(`MESSAGE.REDIRECT_${fromPage}`);
    }

    this.generateToast(ToastType.REDIRECT, newMessage);
  }

  public generateFailureToast(reason: ConfType, msgParams?: { [k: string]: any }): void {
    let newMessage;

    if (reason === ConfType.SEARCH_MACHINE) {
      newMessage = this.translateService.instant('MESSAGE.SEARCH_MACHINE_FAIL', { fleetNumber: msgParams.fleetNumber });
    } else {
      const formatString = (reason as string).toUpperCase();
      newMessage = this.translateService.instant(`MESSAGE.${formatString}_FAIL`);
    }

    this.generateToast(ToastType.WARNING, newMessage);
  }

  public generateSuccessToast(reason: ConfType, msgParams?: { [k: string]: any }): void {
    let newMessage;

    switch (reason) {
      case ConfType.STATUS_IN_TRANSPORT:
      case ConfType.STATUS_DELIVERED: {
        const formatString = (reason as string).toUpperCase();
        const status = this.translateService.instant(`STATUS.${formatString}`);
        newMessage = this.translateService.instant('MESSAGE.SET_STATUS', { status });
        break;
      }

      case ConfType.SWITCH_MACHINE: {
        newMessage = this.translateService.instant('MESSAGE.SWITCH_MACHINE_SUCCESS', {
          oldFleetNumber: msgParams.oldFleetNumber,
          newFleetNumber: msgParams.newFleetNumber
        });
        break;
      }

      case ConfType.SEARCH_MACHINE: {
        newMessage = this.translateService.instant('MESSAGE.SEARCH_MACHINE_SUCCESS', {
          fleetNumber: msgParams.fleetNumber
        });
        break;
      }

      case ConfType.PROTOCOL_SUCCESS_SAVE_DRAFT: {
        newMessage = this.translateService.instant('MESSAGE.PROTOCOL_SUCCESS_SAVE_DRAFT');
        break;
      }

      default: {
        const formatString = (reason as string).toUpperCase();
        newMessage = this.translateService.instant(`MESSAGE.${formatString}_SUCCESS`);
      }
    }

    this.generateToast(ToastType.SUCCESS, newMessage);
  }

  public generateToast(type: ToastType, message: string) {
    const newId = this.toasts.length ? this.toasts[this.toasts.length - 1].id + 1 : 1;
    const newToast: Toast = {
      id: newId,
      type,
      message
    };
    const reloadToastExists = this.toasts.some((toast) => toast.type === ToastType.RELOAD);

    if (type !== ToastType.RELOAD || (type === ToastType.RELOAD && !reloadToastExists)) {
      this.store.dispatch(new AddToast(newToast));
    }

    return newId;
  }

  public dismissToast(id): void {
    this.store.dispatch(new DismissToast(id));
  }
}
